/**
 * AI強化学習ダッシュボード＋ベンチマーク（2026-07-12）。
 * ・学習状況：教師データ件数／修正ラベル／型番学習量／ベンチ精度
 * ・抽出的中率（v_ai_extraction_accuracy）とAIの弱点（v_ai_correction_stats）
 * ・ベンチマーク：写真→analyze-photos実行→AI抽出表示→正解入力→保存（精度計測データを蓄積）
 * すべて内部指標。価格は扱わない。
 */
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Brain, Wrench, Ruler, Database, Camera, Play, Save, Target } from "lucide-react";
import { adminApi, type BenchExtract } from "@/lib/api";

function fileToBase64(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => { const s = String(fr.result || ""); res(s.includes(",") ? s.slice(s.indexOf(",") + 1) : s); };
    fr.onerror = rej;
    fr.readAsDataURL(f);
  });
}

export function AiLearningPage() {
  const { data: stats } = useQuery({ queryKey: ["ai-stats"], queryFn: () => adminApi.aiLearningStats() });
  const { data: bench } = useQuery({ queryKey: ["ai-bench-acc"], queryFn: () => adminApi.benchmarkAccuracy() });

  return (
    <div className="px-4 py-4 space-y-5">
      <h2 className="text-lg font-semibold flex items-center gap-2"><Brain size={20} /> AI強化学習</h2>

      {!stats ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={<Database size={18} />} label="教師データ" value={`${stats.samples}件`} />
            <Stat icon={<Wrench size={18} />} label="修正ラベル" value={`${stats.corrections}件`} accent />
            <Stat icon={<Database size={18} />} label="型番学習量" value={`${stats.modelCatalog}機種`} />
            <Stat icon={<Target size={18} />} label="ベンチ型番一致率" value={bench?.model_accuracy_pct != null ? `${bench.model_accuracy_pct}%` : "—"} />
          </section>

          <section className="rounded-xl border border-[var(--color-border)] p-4 space-y-2">
            <h3 className="text-sm font-bold">AI抽出の的中率（確定値との一致）</h3>
            {stats.extractionAccuracy.length === 0 ? (
              <p className="text-xs text-ink-soft dark:text-white/50 py-2">まだラベル付きデータがありません。案件が回ると蓄積されます。</p>
            ) : (
              <ul className="divide-y divide-[var(--color-border)]">
                {stats.extractionAccuracy.map((a) => (
                  <li key={a.attribute} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-ink-soft dark:text-white/70">{ATTR_LABEL[a.attribute] ?? a.attribute}</span>
                    <span className="tabular-nums">
                      <b>{a.accuracy_pct != null ? `${a.accuracy_pct}%` : "—"}</b>
                      <span className="text-xs text-ink-soft dark:text-white/50 ml-2">({a.n_match}/{a.n_labeled})</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-[var(--color-border)] p-4 space-y-2">
            <h3 className="text-sm font-bold flex items-center gap-2"><Ruler size={15} /> AIの弱点（人手修正が多いフィールド）</h3>
            {stats.correctionByField.length === 0 ? (
              <p className="text-xs text-ink-soft dark:text-white/50 py-2">まだ修正ラベルがありません。業者が「修正」すると自動で貯まります。</p>
            ) : (
              <ul className="divide-y divide-[var(--color-border)]">
                {stats.correctionByField.map((f) => (
                  <li key={f.field} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-ink-soft dark:text-white/70">{ATTR_LABEL[f.field] ?? f.field}</span>
                    <span className="tabular-nums font-semibold">{f.n_corrections}件<span className="text-xs text-ink-soft dark:text-white/50 ml-2">{f.n_cases}案件</span></span>
                  </li>
                ))}
              </ul>
            )}
            {stats.heightError && stats.heightError.n > 0 && (
              <p className="text-[11px] text-ink-soft dark:text-white/60 pt-1">
                設置高さ誤差(MAE)：<b>{stats.heightError.mae_m}m</b>（最大 {stats.heightError.max_err_m}m ／ {stats.heightError.n}件）
              </p>
            )}
          </section>

          <BenchmarkTool />
        </>
      )}
    </div>
  );
}

const ATTR_LABEL: Record<string, string> = {
  model: "型番", model_number: "型番", capacity: "能力", refrigerant: "冷媒",
  install_form: "設置形態", install_height: "設置高さ", power: "電源", piping: "配管",
};

/** 写真→AI実行→正解入力→保存。実案件を待たずに精度を測る。 */
function BenchmarkTool() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<Array<{ mimeType: string; data: string }>>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [extract, setExtract] = useState<BenchExtract | null>(null);
  const [label, setLabel] = useState("");
  const [tModel, setTModel] = useState("");
  const [tCap, setTCap] = useState("");
  const [tHeight, setTHeight] = useState("");

  const { data: recent } = useQuery({ queryKey: ["ai-bench-recent"], queryFn: () => adminApi.recentBenchmarks(15) });

  const runMut = useMutation({
    mutationFn: () => adminApi.runBenchmark(images),
    onSuccess: (x) => {
      setExtract(x);
      setTModel([x.maker, x.model].filter(Boolean).join(" ") || "");
      setTCap(x.capacity ?? "");
      setTHeight(typeof x.install_height_m === "number" ? String(x.install_height_m) : "");
    },
  });
  const saveMut = useMutation({
    mutationFn: () => adminApi.saveBenchmark({
      label: label.trim() || null,
      extracted: extract!,
      truth_model: tModel.trim() || null,
      truth_capacity: tCap.trim() || null,
      truth_install_height_m: tHeight.trim() ? Number(tHeight) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-bench-recent"] });
      qc.invalidateQueries({ queryKey: ["ai-bench-acc"] });
      qc.invalidateQueries({ queryKey: ["ai-stats"] });
      setExtract(null); setImages([]); setPreviews([]); setLabel("");
    },
  });

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 8);
    const b64 = await Promise.all(arr.map(async (f) => ({ mimeType: f.type || "image/jpeg", data: await fileToBase64(f) })));
    setImages(b64);
    setPreviews(arr.map((f) => URL.createObjectURL(f)));
    setExtract(null);
  };

  return (
    <section className="rounded-xl border border-brand-300 dark:border-brand-700 p-4 space-y-3">
      <h3 className="text-sm font-bold flex items-center gap-2"><Camera size={16} className="text-brand-600" /> AIベンチマーク（写真で精度テスト）</h3>
      <p className="text-[11px] text-ink-soft dark:text-white/60">
        銘板やエアコンの写真を選び「AI実行」。抽出結果に正解を入れて保存すると、型番一致率・設置高さ誤差に反映されます。
      </p>

      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
      <div className="flex gap-2">
        <button onClick={() => inputRef.current?.click()} className="flex items-center gap-1.5 text-sm border border-[var(--color-border)] rounded-lg px-3 py-2">
          <Camera size={15} /> 写真を選ぶ{images.length > 0 ? `（${images.length}枚）` : ""}
        </button>
        <button onClick={() => runMut.mutate()} disabled={images.length === 0 || runMut.isPending}
          className="flex items-center gap-1.5 text-sm bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-bold rounded-lg px-3 py-2">
          {runMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} AI実行
        </button>
      </div>
      {previews.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {previews.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover rounded-lg border border-[var(--color-border)]" />)}
        </div>
      )}
      {runMut.error && <p className="text-xs text-red-600">{(runMut.error as Error).message}</p>}

      {extract && (
        <div className="rounded-lg bg-brand-50/60 dark:bg-brand-800/20 border border-brand-200 dark:border-brand-700 p-3 space-y-2">
          <p className="text-xs font-bold">AI抽出結果</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <KV k="メーカー" v={extract.maker} /><KV k="型番" v={extract.model} />
            <KV k="能力" v={extract.capacity} /><KV k="設置形態" v={extract.install_form} />
            <KV k="冷媒" v={extract.refrigerant} /><KV k="電源" v={extract.power} />
            <KV k="配管" v={extract.piping} />
            <KV k="推定設置高さ" v={typeof extract.install_height_m === "number" ? `約${extract.install_height_m}m` : null} />
            <KV k="カタログ照合" v={extract.catalog_matched ? "一致" : "不一致"} />
          </div>
          <div className="pt-1 space-y-2">
            <p className="text-xs font-bold">正解を入力（保存で精度に反映）</p>
            <Field label="メモ"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="例: 三菱重工 SRK40 / 現場A" className="inp" /></Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="正解 型番"><input value={tModel} onChange={(e) => setTModel(e.target.value)} className="inp" /></Field>
              <Field label="正解 能力"><input value={tCap} onChange={(e) => setTCap(e.target.value)} className="inp" /></Field>
              <Field label="正解 高さ(m)"><input value={tHeight} onChange={(e) => setTHeight(e.target.value)} inputMode="decimal" className="inp" /></Field>
            </div>
            <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
              className="flex items-center gap-1.5 text-sm bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-bold rounded-lg px-3 py-2">
              {saveMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} ベンチマークを保存
            </button>
            {saveMut.error && <p className="text-xs text-red-600">{(saveMut.error as Error).message}</p>}
          </div>
        </div>
      )}

      {recent && recent.length > 0 && (
        <div className="pt-1">
          <p className="text-xs font-bold mb-1">最近のベンチマーク</p>
          <ul className="divide-y divide-[var(--color-border)] text-xs">
            {recent.map((b) => {
              const hit = b.truth_model && b.ai_model &&
                (norm(b.ai_model).includes(norm(b.truth_model)) || norm(b.truth_model).includes(norm(b.ai_model)));
              return (
                <li key={b.id} className="flex items-center justify-between py-1.5 gap-2">
                  <span className="truncate">
                    <span className="text-ink-soft dark:text-white/60">{b.label || "（メモなし）"}</span>
                    <span className="ml-2">AI:{b.ai_model || "—"} / 正:{b.truth_model || "—"}</span>
                  </span>
                  <span className={`shrink-0 font-bold ${b.truth_model ? (hit ? "text-emerald-600" : "text-red-500") : "text-ink-soft"}`}>
                    {b.truth_model ? (hit ? "○" : "×") : "-"}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid var(--color-border);border-radius:8px;padding:6px 8px;background:transparent;font-size:13px}`}</style>
    </section>
  );
}

function norm(s: string) { return s.toUpperCase().replace(/[\s\-‐–—ー・/／]/g, ""); }

function KV({ k, v }: { k: string; v?: string | null }) {
  return <div className="flex justify-between gap-2"><span className="text-ink-soft dark:text-white/60">{k}</span><span className="font-medium text-right break-all">{v || "—"}</span></div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-xs space-y-1"><span className="text-ink-soft dark:text-white/60">{label}</span>{children}</label>;
}
function Stat({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-brand-300 dark:border-brand-700 bg-brand-50/60 dark:bg-brand-800/20" : "border-[var(--color-border)]"}`}>
      <div className="flex items-center gap-2 text-ink-soft dark:text-white/60">{icon}<span className="text-xs">{label}</span></div>
      <div className="text-xl font-black mt-1">{value}</div>
    </div>
  );
}
